import {Record, Map, List} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';
import {type Permission} from './types';
import {type GetRolesAndPermissionsQuery} from './__generated__/queries.api-types';

type PermissionWithParent = Permission & {
    parent: Permission | null;
};

class RolePermissionState extends Record<{
    readonly granted: boolean;
    readonly locked: boolean;
    readonly missing: boolean;
    readonly partial: boolean;
}>({
    granted: false,
    locked: true,
    missing: true,
    partial: false,
}) {}

export interface PermissionsStoreState {
    readonly loading: boolean;
    readonly pristine: boolean;
    readonly flattenedPermissions: List<PermissionWithParent>;
    readonly permissions: Permission[];
    readonly roles: GetRolesAndPermissionsQuery['roles']['list']['roles'];
    readonly rolePermissions: Map<string, Map<string, RolePermissionState>>; // permissionId -> roleId -> role permission info
    readonly comparedRoles: List<string>;
}

export class PermissionsState extends Record<PermissionsStoreState>({
    loading: false,
    pristine: true,
    flattenedPermissions: List<PermissionWithParent>(),
    permissions: [],
    roles: [],
    rolePermissions: Map<string, Map<string, RolePermissionState>>(),
    comparedRoles: List<string>(),
}) {}

export default makeReducer<PermissionsState>(
    [
        match(Actions.fetchData, state => {
            return state.merge({
                loading: true,
            });
        }),
        match(Actions.setInitialState, (state, {payload}) => {
            const flattenedPermissions = traverse(payload.permissions);

            return state.merge({
                loading: false,
                pristine: true,
                flattenedPermissions,
                permissions: payload.permissions,
                roles: payload.roles,
                rolePermissions: makeRolePermissions(flattenedPermissions, payload.roles),
                comparedRoles: List(payload.roles.map(role => role.roleId)),
            });
        }),
        match(Actions.fetchDataFailed, state => {
            return state.merge({
                loading: false,
            });
        }),
        match(Actions.updateRolePermissions, (state, {payload}) => {
            return state.merge({
                rolePermissions: makeRolePermissions(state.flattenedPermissions, payload.roles, state.rolePermissions),
                pristine: true,
            });
        }),
        match(Actions.togglePermission, (state, {payload}) => {
            const updatedRolePermissions = propagateToggle(
                state.rolePermissions,
                state.flattenedPermissions,
                payload.permissionId,
                payload.roleId,
            );

            if (updatedRolePermissions) {
                return state.merge({
                    rolePermissions: updatedRolePermissions,
                    pristine: false,
                });
            } else {
                return state;
            }
        }),
        match(Actions.addToComparison, (state, {payload}) => {
            return state.merge({
                comparedRoles: state.comparedRoles.push(payload.roleId),
            });
        }),
        match(Actions.removeFromComparison, (state, {payload}) => {
            return state.merge({
                comparedRoles: state.comparedRoles.filter(roleId => roleId !== payload.roleId),
            });
        }),
        match(Actions.reset, () => {
            return new PermissionsState();
        }),
        match(Actions.setComparedRoleIds, (state, {payload}) => {
            return state.merge({
                comparedRoles: payload.roleIds,
            });
        }),
        match(Actions.addOneAndRemoveOneFromComparison, (state, {payload}) => {
            const [, ...comparedRolesToKeep] = state.comparedRoles;
            const newComparedRoles = List([...comparedRolesToKeep, payload.roleId]);
            return state.merge({
                comparedRoles: newComparedRoles,
            });
        }),
    ],
    new PermissionsState(),
);

// Toggles a permission (and its consequences) in the entire permission 'tree'
const propagateToggle = (
    rolePermissions: Map<string, Map<string, RolePermissionState>>,
    flattenedPermissions: List<PermissionWithParent>,
    permissionIdToToggle: string,
    roleId: string,
) => {
    const permission = flattenedPermissions.find(p => p.id === permissionIdToToggle);

    if (!permission) {
        return undefined;
    }

    const subPermissions = traverseHelper(permission).concat(permission);
    const newValue = !rolePermissions.get(permission.id)!.get(roleId)!.granted;

    const updatedRolePermissions = subPermissions.reduce(
        (result: Map<string, Map<string, RolePermissionState>> | undefined, permission) => {
            if (result === undefined) {
                return undefined;
            }

            return togglePermission(result, permission.id, roleId, newValue);
        },
        rolePermissions,
    );

    // Work up the tree
    const parents = parentList(flattenedPermissions, permission).rest();
    const updatedRolePermissionsUpTheTree = parents.reduce(
        (result: Map<string, Map<string, RolePermissionState>> | undefined, permission) => {
            if (!result) {
                return undefined;
            }

            const grantedOnLevelForRole = (permission.permissions || []).map(subPermissions => {
                const rolePermissionForAllRoles = result.get(subPermissions.id);

                if (!rolePermissionForAllRoles) {
                    return {
                        granted: false,
                        partial: false,
                    };
                }

                const permissionForRole = rolePermissionForAllRoles.get(roleId);

                return {
                    granted: permissionForRole ? permissionForRole.granted : false,
                    partial: permissionForRole ? permissionForRole.granted || permissionForRole.partial : false,
                };
            });

            const newGrantedValue = grantedOnLevelForRole.every(grant => grant.granted);
            const newPartialValue = grantedOnLevelForRole.find(grant => grant.granted || grant.partial) !== undefined;

            const currentValue = result.get(permission.id)?.get(roleId);
            const currentGrantedValue = currentValue?.granted || false;
            const currentPartialValue = currentValue?.partial || false;

            /*
                Due to the rule that a permission is not the sum of its sub-permissions,
                only un-granting a permission based on its subpermissions works up the tree.
                Partial assignment (defined by having at least sub permissions granted in any depth of the tree) always travels up the tree.
            */
            const didPartialChange = newPartialValue !== currentPartialValue;
            const didGrantedChange = newGrantedValue !== currentGrantedValue;
            const shouldTogglePartial = didPartialChange;
            const shouldToggleGranted = newGrantedValue === false && didGrantedChange;

            if (shouldTogglePartial || shouldToggleGranted) {
                return togglePermission(
                    result,
                    permission.id,
                    roleId,
                    shouldToggleGranted ? newGrantedValue : currentGrantedValue,
                    shouldTogglePartial ? newPartialValue : currentPartialValue,
                );
            } else {
                return result;
            }
        },
        updatedRolePermissions,
    );

    return updatedRolePermissionsUpTheTree;
};

// Toggles a single permission for a single role
const togglePermission = (
    rolePermissions: Map<string, Map<string, RolePermissionState>>,
    permission: string,
    roleId: string,
    granted: boolean,
    partial = false,
) => {
    const rolePermissionForAllRoles = rolePermissions.get(permission);

    if (!rolePermissionForAllRoles) {
        return undefined;
    }

    const permissionForRole = rolePermissionForAllRoles.get(roleId);

    if (!permissionForRole) {
        return undefined;
    }

    const updatedPermissionForRole = permissionForRole.merge({granted, partial});
    const updatedRolePermissionForAllRoles = rolePermissionForAllRoles.set(roleId, updatedPermissionForRole);
    return rolePermissions.set(permission, updatedRolePermissionForAllRoles);
};

// Builds a map from permission to roleId to an object that says if its granted or not (and some other information)
const makeRolePermissions = (
    flattenedPermissions: List<Permission>,
    roles: GetRolesAndPermissionsQuery['roles']['list']['roles'],
    existing?: Map<string, Map<string, RolePermissionState>>,
) => {
    const newRolePermissions = Map(
        flattenedPermissions.map(permission => {
            return [
                permission.id,
                Map(
                    roles.map(role => {
                        const rolePermission = role.permissions.find(rp => rp.permissionId === permission.id);
                        return [
                            role.roleId,
                            new RolePermissionState({
                                granted: rolePermission ? rolePermission.granted : false,
                                locked: rolePermission ? rolePermission.locked : true,
                                missing: !rolePermission,
                                partial:
                                    permission.permissions && permission.permissions.length >= 0
                                        ? traverse(permission.permissions)
                                              .map(p => {
                                                  const childPermission = role.permissions.find(
                                                      rp => rp.permissionId === p.id,
                                                  );
                                                  if (childPermission) {
                                                      return childPermission.granted;
                                                  } else {
                                                      return false;
                                                  }
                                              })
                                              .find(granted => granted)
                                        : false,
                            }),
                        ];
                    }),
                ),
            ] as [string, Map<string, RolePermissionState>];
        }),
    );

    if (existing) {
        return existing.mergeDeep(newRolePermissions);
    } else {
        return newRolePermissions;
    }
};

const traverseHelper = (permission: Permission): List<PermissionWithParent> => {
    if (!permission.permissions) {
        return List();
    } else {
        return List(permission.permissions).flatMap(p => traverseHelper(p).insert(0, {...p, parent: permission}));
    }
};

export const traverse = (permissions: Permission[]): List<PermissionWithParent> => {
    return List(permissions).flatMap(p => traverseHelper(p).insert(0, {...p, parent: null}));
};

const parentList = (
    flattenedPermissions: List<PermissionWithParent>,
    permission: PermissionWithParent,
): List<Permission> => {
    if (permission.parent === null) {
        return List.of(permission);
    } else {
        const parent = flattenedPermissions.find(p => p.id === permission.parent!.id);

        if (!parent) {
            return List.of(permission);
        }

        return List.of(permission).flatMap(p => parentList(flattenedPermissions, parent).insert(0, p));
    }
};
